/* eslint-disable no-unused-vars */
import { KeyboardArrowDownRounded } from '@mui/icons-material';
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Image } from 'app/components';
import { useAsyncAction, usePermission, useStateRef } from 'app/services';
import { trailerLoad } from 'app/store/trailer';
import { transportGroupLoad, transportModelLoad, transportTypeLoad } from 'app/store/transport';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';

import { detectPhoneNumberCode } from '../../utils/phoneDetection';

const TransportForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const editPermission = usePermission(['create', 'update', 'delete']);
  const isEngineer = usePermission(['read+']);
  const [numberOfTank, setNumberOfTank] = useState(defaultValues ? defaultValues.tank.length : '');

  const { asyncAction: fetchGroupData, isLoading: isFetchGroupLoading } = useAsyncAction(transportGroupLoad);
  const { asyncAction: fetchTypeData, isLoading: isFetchTypeLoading } = useAsyncAction(transportTypeLoad);
  const { asyncAction: fetchModelData, isLoading: isFetchModelLoading } = useAsyncAction(transportModelLoad);
  const { asyncAction: fetchTrailerData, isLoading: isFetchTrailerLoading } = useAsyncAction(trailerLoad);
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);
  const { items: trailerItems } = useSelector((state) => state.trailer);
  const { groupItems, typeItems, modelItems } = useSelector((state) => state.transport);

  const { defaultValues, onSubmit } = props;
  const { register, watch, reset, control, formState, handleSubmit, setValue } = useForm({
    defaultValues: {
      name: defaultValues ? defaultValues.name : '',
      number: defaultValues ? defaultValues?.number || '' : '',
      number_country: defaultValues
        ? (defaultValues?.number_country ?? config.transportNumberOptions[0].code)
        : config.transportNumberOptions[0].code,
      trailer_id: defaultValues ? (defaultValues?.trailer_id ?? '') : '',
      vin: defaultValues ? (defaultValues?.vin ?? '') : '',
      type_id: defaultValues ? (defaultValues?.type_id ?? '') : '',
      model_id: defaultValues ? (defaultValues?.model_id ?? '') : '',
      comment: defaultValues ? (defaultValues?.comment ?? '') : '',
      volume: defaultValues ? defaultValues.volume : '',
      retransmission_lukoil_prefix: defaultValues ? (defaultValues?.retransmission_lukoil_prefix ?? '') : '',
      retransmission_lukoil_id: defaultValues ? (defaultValues?.retransmission_lukoil_id ?? '') : '',
      retransmission_lukoil_on: defaultValues ? defaultValues?.retransmission_lukoil_on : 0,
      retransmission_rnis_prefix: defaultValues ? (defaultValues?.retransmission_rnis_prefix ?? '') : '',
      retransmission_rnis_id: defaultValues ? (defaultValues?.retransmission_rnis_id ?? '') : '',
      retransmission_rnis_on: defaultValues ? defaultValues?.retransmission_rnis_on : 0,
      imei: defaultValues ? (defaultValues?.imei ?? '') : '',
      device_type: defaultValues
        ? (defaultValues?.device_type ?? config.transportDeviceOptions[0].type)
        : config.transportDeviceOptions[0].type,
      sim0: defaultValues ? defaultValues?.sim0 || '' : '',
      number_sim0: defaultValues
        ? defaultValues.sim0
          ? detectPhoneNumberCode(defaultValues.sim0)
          : config.transportPhoneNumberOptions[0].code
        : config.transportPhoneNumberOptions[0].code,
      sim1: defaultValues ? defaultValues?.sim1 || '' : '',
      number_sim1: defaultValues
        ? defaultValues.sim1
          ? detectPhoneNumberCode(defaultValues.sim1)
          : config.transportPhoneNumberOptions[0].code
        : config.transportPhoneNumberOptions[0].code,
      group_id: defaultValues ? (defaultValues?.group_id ?? '') : '',
      tank: [],
      wialon_type: defaultValues ? defaultValues?.wialon_type : null,
      wialon_imei: defaultValues ? (defaultValues?.wialon_imei ?? '') : '',
    },
  });

  const numberCountryWatcher = watch('number_country');
  const imeiWatcher = watch('imei');
  const retransmissionLukoilWatcher = watch('retransmission_lukoil_on');
  const retransmissionRnisWatcher = watch('retransmission_rnis_on');
  const numberSim0Watcher = watch('number_sim0');
  const numberSim1Watcher = watch('number_sim1');
  const { fields, append, remove } = useFieldArray({ name: 'tank', control });

  const handleTankChange = (event) => {
    setNumberOfTank(event.target.value);
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          name: data.name,
          number: data.number ? data.number.replace(/[\s]+/g, '') : undefined,
          number_country: data.number_country ? (data.number ? data.number_country : undefined) : undefined,
          trailer_id: data.trailer_id ? Number(data.trailer_id) : undefined,
          vin: data.vin ? data.vin : undefined,
          type_id: data.type_id ? Number(data.type_id) : undefined,
          model_id: data.model_id ? Number(data.model_id) : undefined,
          comment: data.comment ? String(data.comment) : undefined,
          volume: String(data.volume),
          retransmission_lukoil_prefix: data.retransmission_lukoil_prefix ? data.retransmission_lukoil_prefix : undefined,
          retransmission_lukoil_id: data.retransmission_lukoil_id ? data.retransmission_lukoil_id : undefined,
          retransmission_lukoil_on: Number(data.retransmission_lukoil_on),
          retransmission_rnis_prefix: data.retransmission_rnis_prefix ? data.retransmission_rnis_prefix : undefined,
          retransmission_rnis_id: data.retransmission_rnis_id ? data.retransmission_rnis_id : undefined,
          retransmission_rnis_on: Number(data.retransmission_rnis_on),
          imei: data.imei ? data.imei : undefined,
          device_type: data.device_type,
          sim0: data.sim0 ? data.sim0 : undefined,
          sim1: data.sim1 ? data.sim1 : undefined,
          tank: data.tank,
          group_id: data.group_id ? Number(data.group_id) : undefined,
          wialon_type: data.wialon_type ? Number(data.wialon_type) : undefined,
          wialon_imei: data.wialon_imei ? data.wialon_imei : undefined,
        })
      )();
    },
  }));

  useLayoutEffect(() => {
    let timerId;
    if (firstUpdateRef.current !== undefined) {
      timerId = setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
    return () => clearTimeout(timerId);
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        name: defaultValues.name,
        number: defaultValues.number ? defaultValues.number : '',
        number_country: defaultValues.number_country ? defaultValues.number_country : config.transportNumberOptions[0].code,
        trailer_id: defaultValues?.trailer_id ?? '',
        vin: defaultValues?.vin ?? '',
        type_id: defaultValues?.type_id ?? '',
        model_id: defaultValues?.model_id ?? '',
        comment: defaultValues?.comment ?? '',
        volume: defaultValues.volume,
        retransmission_lukoil_prefix: defaultValues?.retransmission_lukoil_prefix ?? '',
        retransmission_lukoil_id: defaultValues?.retransmission_lukoil_id ?? '',
        retransmission_lukoil_on: defaultValues.retransmission_lukoil_on,
        retransmission_rnis_prefix: defaultValues?.retransmission_rnis_prefix ?? '',
        retransmission_rnis_id: defaultValues?.retransmission_rnis_id ?? '',
        retransmission_rnis_on: defaultValues.retransmission_rnis_on,
        imei: defaultValues?.imei ?? '',
        device_type: defaultValues?.device_type ?? config.transportDeviceOptions[0].type,
        sim0: defaultValues?.sim0 ?? '',
        number_sim0: defaultValues?.sim0 ? detectPhoneNumberCode(defaultValues.sim0) : config.transportPhoneNumberOptions[0].code,
        sim1: defaultValues?.sim1 ?? '',
        number_sim1: defaultValues?.sim1 ? detectPhoneNumberCode(defaultValues.sim1) : config.transportPhoneNumberOptions[0].code,
        tank: defaultValues.tank.map(([volume, type]) => ({
          volume,
          type,
        })),
        group_id: defaultValues?.group_id ?? '',
        wialon_imei: defaultValues?.wialon_imei ?? '',
      });

      setNumberOfTank(defaultValues.tank.length);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (!firstUpdateRef.current) {
      const countFields = fields.length;

      if (numberOfTank > countFields) {
        for (let i = countFields; i < numberOfTank; i++) {
          const [volume, type] = defaultValues?.tank[i] ? defaultValues.tank[i] : ['', ''];
          append({ volume: volume, type: type }, { shouldFocus: false });
        }
      } else {
        for (let i = countFields; i > numberOfTank; i--) {
          remove(i - 1);
        }
      }
    }
  }, [numberOfTank]);

  useEffect(() => {
    if (defaultValues?.number_country && numberCountryWatcher !== defaultValues?.number_country) {
      setValue('number', '');
    }
  }, [numberCountryWatcher]);

  useEffect(() => {
    if (defaultValues?.number_sim0 && numberSim0Watcher !== defaultValues?.number_sim0) {
      setValue('sim0', '');
    }
  }, [numberSim0Watcher]);

  useEffect(() => {
    if (defaultValues?.number_sim1 && numberSim1Watcher !== defaultValues?.number_sim1) {
      setValue('sim1', '');
    }
  }, [numberSim1Watcher]);

  useEffect(() => {
    fetchGroupData();
    fetchTypeData();
    fetchModelData();
    fetchTrailerData();
  }, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl
            error={!!formState.errors.name}
            margin='normal'
            variant='outlined'
            sx={{
              width: '100%',
            }}>
            <InputLabel disableAnimation={firstUpdate}>Название</InputLabel>
            <OutlinedInput
              label='Название'
              variant='outlined'
              notched={firstUpdate}
              readOnly={!(editPermission || isEngineer)}
              fullWidth
              placeholder='Название'
              {...register('name', {
                required: true,
                minLength: 3,
              })}></OutlinedInput>
            <FormHelperText>Используйте название транспортного средства для отображения в списках</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {(() => {
          const { mask, formatChars, pattern, placeholder, checkPattern } =
            config.transportNumberOptions.find(({ code }) => code === numberCountryWatcher) || {};
          return (
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <FormControl error={!!formState.errors.number} sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                    <InputLabel disableAnimation={firstUpdate}>Регистрационный номер</InputLabel>
                    <Controller
                      name='number'
                      control={control}
                      render={({ field }) => {
                        const isCheckPattern = field.value.length > 0;
                        const checkPatternId =
                          mask.length > 1 ? (isCheckPattern ? checkPattern.findIndex((pattern) => pattern.test(field.value)) : -1) : 0;

                        return (
                          <InputMask
                            mask={mask.length > 1 ? (checkPatternId !== -1 ? mask[checkPatternId] : '**') : mask[0]}
                            maskChar=''
                            readOnly={!editPermission}
                            formatChars={{
                              9: '[0-9]',
                              '*': `[0-9,${formatChars}]`,
                              A: `[${formatChars}]`,
                            }}
                            {...register(field.name, {
                              pattern: mask.length > 1 ? (checkPatternId !== -1 ? pattern[checkPatternId] : pattern[0]) : pattern[0],
                            })}
                            {...field}>
                            {(inputProps) => (
                              <OutlinedInput
                                label='Регистрационный номер'
                                variant='outlined'
                                notched={firstUpdate}
                                fullWidth
                                placeholder={placeholder.join(' или ')}
                                {...inputProps}></OutlinedInput>
                            )}
                          </InputMask>
                        );
                      }}></Controller>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                    <Controller
                      name='number_country'
                      control={control}
                      render={({ field }) => (
                        <Select
                          label=''
                          IconComponent={KeyboardArrowDownRounded}
                          readOnly={!editPermission}
                          MenuProps={{
                            sx: {
                              '.MuiPaper-root': {
                                maxHeight: 200,
                                overflow: 'auto',
                              },
                              '.MuiPaper-root:before': {
                                display: 'none',
                              },
                              '.MuiMenuItem-root': {
                                gap: '12px',
                                justifyContent: 'space-between',
                              },
                              img: {
                                width: '20px',
                                height: '20px',
                              },
                            },
                          }}
                          sx={{
                            '.MuiTypography-root': {
                              display: 'none',
                            },
                            img: {
                              width: '20px',
                              height: '20px',
                            },
                          }}
                          value={field.value}
                          onChange={field.onChange}
                          {...register(field.name, {
                            required: true,
                          })}
                          {...field}>
                          {config.transportNumberOptions.map((value, key) => (
                            <MenuItem key={key} disableRipple value={value.code}>
                              <Typography variant='body2'>{value.title}</Typography>
                              <Image name={`${value.code}.svg`} alt={value.code} />
                            </MenuItem>
                          ))}
                        </Select>
                      )}></Controller>
                  </FormControl>
                </Grid>
              </Grid>
              <FormHelperText>Используйте регистрационный номер в формате {placeholder.join(', ')}</FormHelperText>
            </Grid>
          );
        })()}
        <Grid item xs={6}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            <Controller
              name='trailer_id'
              control={control}
              render={({ field }) => (
                <Autocomplete
                  disabled={!editPermission || isFetchTrailerLoading}
                  options={trailerItems}
                  getOptionLabel={(option) =>
                    option && option.number ? `${option.number.substring(0, 6)} ${option.number.substring(6)}` : ''
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={trailerItems.find((item) => item.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.id : '');
                  }}
                  getOptionKey={(option) => option.id}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) => {
                      if (!inputValue) return true;
                      // Ищем в исходном номере (без пробелов)
                      const searchString = inputValue.toLowerCase().replace(/\s/g, '');
                      const optionNumber = option.number.toLowerCase().replace(/\s/g, '');

                      return optionNumber.includes(searchString);
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Регистрационный номер прицепа'
                      variant='outlined'
                      error={!!formState.errors.trailer_id}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isFetchTrailerLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText='Нет прицепов'
                  readOnly={!editPermission}
                />
              )}
            />
            <FormHelperText>Выберите регистрационный номер прицепа транспортного средства</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Примечание</InputLabel>
            {isFetchTrailerLoading && (
              <OutlinedInput
                disabled
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchTrailerLoading && (
              <Controller
                name='comment'
                control={control}
                render={({ field }) => (
                  <OutlinedInput
                    label='Для заметок'
                    variant='outlined'
                    notched={firstUpdate}
                    fullWidth
                    placeholder=''
                    value={field.value}
                    onChange={field.onChange}
                    {...register(field.name, {
                      minLength: 0,
                      maxLength: 500,
                    })}></OutlinedInput>
                )}></Controller>
            )}
            <FormHelperText>Используйте данное поле для заметок</FormHelperText>
          </FormControl>
        </Grid>
        {(() => {
          const { mask, formatChars, pattern, placeholder, checkPattern } =
            config.transportNumberOptions.find(({ code }) => code === numberCountryWatcher) || {};
          return (
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <FormControl error={!!formState.errors.number} sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                    <InputLabel disableAnimation={firstUpdate}>Тип ТС</InputLabel>
                    {isFetchTypeLoading && (
                      <OutlinedInput
                        disabled
                        endAdornment={
                          <InputAdornment position='end'>
                            <CircularProgress size={16} />
                          </InputAdornment>
                        }
                      />
                    )}
                    {!isFetchTypeLoading && (
                      <Controller
                        name='type_id'
                        control={control}
                        render={({ field }) => (
                          <Select
                            label='Тип ТС'
                            IconComponent={KeyboardArrowDownRounded}
                            readOnly={!editPermission}
                            MenuProps={{
                              sx: {
                                '.MuiPaper-root': {
                                  maxHeight: 200,
                                  overflow: 'auto',
                                },
                                '.MuiPaper-root:before': {
                                  display: 'none',
                                },
                              },
                            }}
                            {...register(field.name, {
                              pattern: /^[0-9]$/iu,
                            })}
                            value={field.value}
                            notched={firstUpdate}
                            onChange={field.onChange}>
                            <MenuItem disableRipple value=''>
                              Не задан
                            </MenuItem>
                            {typeItems.map((value, key) => (
                              <MenuItem key={key} disableRipple value={value.id}>
                                {value.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}></Controller>
                    )}
                    <FormHelperText>Выберите тип</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                    <InputLabel disableAnimation={firstUpdate}>Модель</InputLabel>
                    {isFetchModelLoading && (
                      <OutlinedInput
                        disabled
                        endAdornment={
                          <InputAdornment position='end'>
                            <CircularProgress size={16} />
                          </InputAdornment>
                        }
                      />
                    )}
                    {!isFetchModelLoading && (
                      <Controller
                        name='model_id'
                        control={control}
                        render={({ field }) => (
                          <Select
                            label='Модель'
                            IconComponent={KeyboardArrowDownRounded}
                            readOnly={!editPermission}
                            MenuProps={{
                              sx: {
                                '.MuiPaper-root': {
                                  maxHeight: 200,
                                  overflow: 'auto',
                                },
                                '.MuiPaper-root:before': {
                                  display: 'none',
                                },
                              },
                            }}
                            {...register(field.name, {
                              pattern: /^[0-9]$/iu,
                            })}
                            value={field.value}
                            notched={firstUpdate}
                            onChange={field.onChange}
                            ref={() => {
                              field.ref({
                                focus: () => ({}),
                              });
                            }}>
                            <MenuItem disableRipple value=''>
                              Не задана
                            </MenuItem>
                            {modelItems.map((value, key) => (
                              <MenuItem key={key} disableRipple value={value.id}>
                                {value.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}></Controller>
                    )}
                    <FormHelperText>Выберите модель</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          );
        })()}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.vin} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>VIN номер</InputLabel>
            <Controller
              name='vin'
              control={control}
              rules={{
                pattern: {
                  value: /^[A-HJ-NPR-Z0-9]{17}$/iu,
                  message: 'VIN номер должен содержать 17 символов',
                },
                minLength: {
                  value: 17,
                  message: 'VIN номер должен содержать 17 символов',
                },
              }}
              render={({ field }) => (
                <InputMask
                  mask='AAAAAAAAAAAAAAAAA'
                  maskChar=''
                  readOnly={!editPermission}
                  formatChars={{
                    A: '[A-HJ-NPR-Z0-9]',
                  }}
                  value={field.value}
                  onChange={field.onChange}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='VIN номер'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='1HGBH41JXMN109186'
                      {...inputProps}
                      inputRef={field.ref}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте VIN номер согласно стандартам ISO 3779-1983 и ISO 3780</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.volume} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Объем бензобака</InputLabel>
            <Controller
              name='volume'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='9999'
                  maskChar=''
                  readOnly={!editPermission}
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,4}$/iu,
                    required: true,
                  })}
                  {...field}
                  ref={() => {
                    field.ref({
                      focus: () => ({}),
                    });
                  }}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Объем бензобака'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='600'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Укажите объем бензобака с максимальной длинной 4 символа</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            '.MuiCheckbox-root': {
              padding: '0 9px',
              '&:not(.Mui-checked) svg': {
                color: '#d5d5d5',
              },
            },
          }}>
          <FormControlLabel
            label='Ретранслировать данные в Лукойл'
            control={
              <Controller
                name='retransmission_lukoil_on'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    size='small'
                    disableRipple
                    disabled={!(editPermission || isEngineer)}
                    checked={!!field.value}
                    onChange={field.onChange}
                    inputRef={field.ref}
                  />
                )}></Controller>
            }
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl
            error={!!formState.errors.retransmission_lukoil_prefix}
            margin='normal'
            variant='outlined'
            disabled={!retransmissionLukoilWatcher}
            fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Префикс ретрансляции Лукойл</InputLabel>
            <Controller
              name='retransmission_lukoil_prefix'
              control={control}
              render={({ field }) => (
                <Select
                  label='Префикс ретрансляции Лукойл'
                  IconComponent={KeyboardArrowDownRounded}
                  readOnly={!(editPermission || isEngineer)}
                  MenuProps={{
                    sx: {
                      '.MuiPaper-root:before': {
                        display: 'none',
                      },
                    },
                  }}
                  {...register(field.name, {
                    pattern: /^([A-Z]{1,4})?$/iu,
                    required: !!retransmissionLukoilWatcher,
                  })}
                  notched={firstUpdate}
                  value={field.value}
                  onChange={field.onChange}
                  ref={() => {
                    field.ref({
                      focus: () => ({}),
                    });
                  }}>
                  {config.retransmissionLukoilPrefix.map((value, key) => (
                    <MenuItem key={key} disableRipple value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              )}></Controller>
            <FormHelperText>Выберите префикс для ретрансляции в Лукойл</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl
            error={!!formState.errors.retransmission_lukoil_id}
            margin='normal'
            variant='outlined'
            disabled={!retransmissionLukoilWatcher}
            fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Идентификатор ретрансляции Лукойл</InputLabel>
            <Controller
              name='retransmission_lukoil_id'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='99999999999999999999'
                  maskChar=''
                  readOnly={!(editPermission || isEngineer)}
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,20}$/iu,
                    required: !!retransmissionLukoilWatcher,
                  })}
                  {...field}
                  ref={() => {
                    field.ref({
                      focus: () => ({}),
                    });
                  }}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Идентификатор ретрансляции Лукойл'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='89660515114886256074'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте ID ретрансляции Лукойл, который соответствует ICCID SIM1</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.retransmission_lukoil_id} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Протокол ретрансляции</InputLabel>
            <Controller
              name='retransmission_rnis_on'
              control={control}
              render={({ field }) => (
                <Select
                  label='Протокол ретрансляции'
                  IconComponent={KeyboardArrowDownRounded}
                  readOnly={!(editPermission || isEngineer)}
                  MenuProps={{
                    sx: {
                      '.MuiPaper-root:before': {
                        display: 'none',
                      },
                    },
                  }}
                  notched={firstUpdate}
                  value={field.value}
                  onChange={field.onChange}
                  ref={() => {
                    field.ref({
                      focus: () => ({}),
                    });
                  }}>
                  <MenuItem value={0}>Не задан</MenuItem>
                  {config.retransmissionProtocol.map((protocol, key) => (
                    <MenuItem key={key} disableRipple value={protocol.value}>
                      <Typography variant='body2'>{protocol.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              )}></Controller>
            <FormHelperText>Выберите протокол</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl
            error={!!formState.errors.retransmission_rnis_prefix}
            margin='normal'
            variant='outlined'
            disabled={!retransmissionRnisWatcher}
            fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Префикс ретрансляции</InputLabel>
            <Controller
              name='retransmission_rnis_prefix'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask={field.value.length > 2 ? 'AAAA999' : 'AAA'}
                  maskChar=''
                  readOnly={!(editPermission || isEngineer)}
                  value={field.value}
                  onChange={field.onChange}
                  formatChars={{
                    9: '[0-9]',
                    A: '[VRNISTAL]',
                  }}
                  {...register(field.name, {
                    pattern: /^(RN|VRN|RNIS(?:0[1-9]|[1-9][0-9]{0,2})|TN|ALL)$/iu,
                    required: !!retransmissionRnisWatcher,
                  })}
                  {...field}
                  ref={() => {
                    field.ref({
                      focus: () => ({}),
                    });
                  }}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Префикс ретрансляции'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='RNIS77 или RN/VRN или TN/ALL'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Укажите префикс для РНИС RNIS+регион | для Роснефть VRN/RN | для Татнефть(EGTS) TN/ALL</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl
            error={!!formState.errors.retransmission_rnis_id}
            margin='normal'
            variant='outlined'
            disabled={!retransmissionRnisWatcher}
            fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Идентификатор ретрансляции</InputLabel>
            <Controller
              name='retransmission_rnis_id'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='9999999999'
                  maskChar=''
                  readOnly={!(editPermission || isEngineer)}
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,10}$/iu,
                    required: !!retransmissionRnisWatcher,
                  })}
                  {...field}
                  ref={() => {
                    field.ref({
                      focus: () => ({}),
                    });
                  }}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Идентификатор ретрансляции РНИС'
                      variant='outlined'
                      notched={firstUpdate}
                      placeholder='8625607400'
                      fullWidth
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте ID для ретрансляции в РНИС, который соответствует последним 10 цифрам ICCID SIM1</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Группа принадлежности</InputLabel>
            {isFetchGroupLoading && (
              <OutlinedInput
                disabled
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchGroupLoading && (
              <Controller
                name='group_id'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Группа принадлежности'
                    IconComponent={KeyboardArrowDownRounded}
                    readOnly={!(editPermission || isEngineer)}
                    MenuProps={{
                      sx: {
                        '.MuiPaper-root': {
                          maxHeight: 200,
                          overflow: 'auto',
                        },
                        '.MuiPaper-root:before': {
                          display: 'none',
                        },
                      },
                    }}
                    {...register(field.name, {
                      pattern: /^[0-9]$/iu,
                    })}
                    value={field.value}
                    notched={firstUpdate}
                    onChange={field.onChange}
                    ref={() => {
                      field.ref({
                        focus: () => ({}),
                      });
                    }}>
                    <MenuItem disableRipple value=''>
                      Не задана
                    </MenuItem>
                    {groupItems.map((value, key) => (
                      <MenuItem key={key} disableRipple value={value.id}>
                        {value.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}></Controller>
            )}
            <FormHelperText>Выберите группу принадлежности транспорта</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={1}>
            <Grid item xs={7}>
              <FormControl error={!!formState.errors.imei} sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                <InputLabel disableAnimation={firstUpdate}>IMEI терминала</InputLabel>
                <Controller
                  name='imei'
                  control={control}
                  render={({ field }) => (
                    <InputMask
                      mask='9999999999999999'
                      maskChar=''
                      readOnly={!editPermission}
                      value={field.value}
                      onChange={field.onChange}
                      {...register(field.name, {
                        pattern: /^[0-9]{15,16}$/iu,
                      })}
                      {...field}
                      ref={() => {
                        field.ref({
                          focus: () => ({}),
                        });
                      }}>
                      {(inputProps) => (
                        <OutlinedInput
                          label='IMEI терминала'
                          variant='outlined'
                          notched={firstUpdate}
                          placeholder='352099001761481'
                          fullWidth
                          {...inputProps}></OutlinedInput>
                      )}
                    </InputMask>
                  )}></Controller>
              </FormControl>
            </Grid>
            <Grid item xs={5}>
              <FormControl sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                <Controller
                  name='device_type'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      label=''
                      IconComponent={KeyboardArrowDownRounded}
                      readOnly={!editPermission}
                      MenuProps={{
                        sx: {
                          '.MuiPaper-root': {
                            maxHeight: 200,
                            overflow: 'auto',
                          },
                          '.MuiPaper-root:before': {
                            display: 'none',
                          },
                          '.MuiMenuItem-root': {
                            gap: '12px',
                            justifyContent: 'space-between',
                          },
                          img: {
                            width: '20px',
                            height: '20px',
                          },
                        },
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      ref={field.ref}>
                      {config.transportDeviceOptions.map((value, key) => (
                        <MenuItem key={key} disableRipple value={value.type}>
                          <Typography variant='body2'>{value.title}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  )}></Controller>
              </FormControl>
            </Grid>
          </Grid>
          <FormHelperText>Используйте IMEI терминала, который соответствует стандарту ISO/IEC 7812</FormHelperText>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6} />
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.wialon_imei} sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>IMEI 2216</InputLabel>
            <Controller
              name='wialon_imei'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='9999999999999999'
                  maskChar=''
                  readOnly={!editPermission}
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{15,16}$/iu,
                  })}
                  {...field}
                  ref={() => {
                    field.ref({
                      focus: () => ({}),
                    });
                  }}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='IMEI 2216'
                      variant='outlined'
                      notched={firstUpdate}
                      placeholder='352099001761481'
                      fullWidth
                      {...inputProps}
                    />
                  )}
                </InputMask>
              )}
            />
          </FormControl>
          <FormHelperText>Используйте IMEI 2216, который соответствует стандарту ISO/IEC 7812</FormHelperText>
        </Grid>
      </Grid>
      {defaultValues && (
        <Grid container spacing={2}>
          <React.Fragment>
            {(() => {
              const {
                mask,
                pattern,
                placeholder = '',
                checkPattern,
              } = config.transportPhoneNumberOptions.find(({ code }) => code === numberSim0Watcher) || {};
              return (
                <Grid item xs={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={8}>
                      <FormControl
                        error={!!formState.errors.sim0}
                        disabled={!imeiWatcher}
                        sx={{ marginBottom: 0 }}
                        margin='normal'
                        variant='outlined'
                        fullWidth>
                        <InputLabel shrink disableAnimation={firstUpdate}>
                          Sim0
                        </InputLabel>
                        <Controller
                          name='sim0'
                          control={control}
                          render={({ field }) => {
                            const isCheckPattern = field.value.length > 0;
                            const checkPatternId =
                              mask.length > 1 ? (isCheckPattern ? checkPattern.findIndex((pattern) => pattern.test(field.value)) : -1) : 0;

                            return (
                              <InputMask
                                mask={mask.length > 1 ? (checkPatternId !== -1 ? mask[checkPatternId] : '**') : mask[0]}
                                maskChar=''
                                readOnly={!editPermission}
                                formatChars={{
                                  0: '[0-9]',
                                }}
                                {...register(field.name, {
                                  pattern: mask.length > 1 ? (checkPatternId !== -1 ? pattern[checkPatternId] : pattern[0]) : pattern[0],
                                })}
                                {...field}>
                                {(inputProps) => (
                                  <OutlinedInput
                                    label='Sim0'
                                    variant='outlined'
                                    notched
                                    fullWidth
                                    placeholder={placeholder.join(' или ')}
                                    onChange={field.onChange}
                                    {...inputProps}
                                    ref={() => {
                                      field.ref({
                                        focus: () => ({}),
                                      });
                                    }}></OutlinedInput>
                                )}
                              </InputMask>
                            );
                          }}></Controller>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                        <Controller
                          name='number_sim0'
                          control={control}
                          render={({ field }) => (
                            <Select
                              label=''
                              IconComponent={KeyboardArrowDownRounded}
                              readOnly={!editPermission}
                              MenuProps={{
                                sx: {
                                  '.MuiPaper-root': {
                                    maxHeight: 200,
                                    overflow: 'auto',
                                  },
                                  '.MuiPaper-root:before': {
                                    display: 'none',
                                  },
                                  '.MuiMenuItem-root': {
                                    gap: '12px',
                                    justifyContent: 'space-between',
                                  },
                                  img: {
                                    width: '20px',
                                    height: '20px',
                                  },
                                },
                              }}
                              sx={{
                                '.MuiTypography-root': {
                                  display: 'none',
                                },
                                img: {
                                  width: '20px',
                                  height: '20px',
                                },
                              }}
                              value={field.value}
                              onChange={field.onChange}
                              {...register(field.name, {
                                required: true,
                              })}
                              {...field}
                              ref={() => {
                                field.ref({
                                  focus: () => ({}),
                                });
                              }}>
                              {config.transportPhoneNumberOptions.map((value, key) => (
                                <MenuItem key={key} disableRipple value={value.code}>
                                  <Typography variant='body2'>{value.title}</Typography>
                                  <Image name={`${value.code}.svg`} alt={value.code} />
                                </MenuItem>
                              ))}
                            </Select>
                          )}></Controller>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <FormHelperText>Используйте номер телефона терминала {placeholder.join(', ')}</FormHelperText>
                </Grid>
              );
            })()}
          </React.Fragment>
          <React.Fragment>
            {(() => {
              const {
                mask,
                pattern,
                placeholder = '',
                checkPattern,
              } = config.transportPhoneNumberOptions.find(({ code }) => code === numberSim1Watcher) || {};
              return (
                <Grid item xs={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={8}>
                      <FormControl
                        error={!!formState.errors.sim0}
                        disabled={!imeiWatcher}
                        sx={{ marginBottom: 0 }}
                        margin='normal'
                        variant='outlined'
                        fullWidth>
                        <InputLabel shrink disableAnimation={firstUpdate}>
                          Sim1
                        </InputLabel>
                        <Controller
                          name='sim1'
                          control={control}
                          render={({ field }) => {
                            const isCheckPattern = field.value.length > 0;
                            const checkPatternId =
                              mask.length > 1 ? (isCheckPattern ? checkPattern.findIndex((pattern) => pattern.test(field.value)) : -1) : 0;

                            return (
                              <InputMask
                                mask={mask.length > 1 ? (checkPatternId !== -1 ? mask[checkPatternId] : '**') : mask[0]}
                                maskChar=''
                                readOnly={!editPermission}
                                formatChars={{
                                  0: '[0-9]',
                                }}
                                {...register(field.name, {
                                  pattern: mask.length > 1 ? (checkPatternId !== -1 ? pattern[checkPatternId] : pattern[0]) : pattern[0],
                                })}
                                {...field}>
                                {(inputProps) => (
                                  <OutlinedInput
                                    label='Sim0'
                                    variant='outlined'
                                    notched
                                    fullWidth
                                    placeholder={placeholder.join(' или ')}
                                    onChange={field.onChange}
                                    {...inputProps}
                                    ref={() => {
                                      field.ref({
                                        focus: () => ({}),
                                      });
                                    }}></OutlinedInput>
                                )}
                              </InputMask>
                            );
                          }}></Controller>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl sx={{ marginBottom: 0 }} margin='normal' variant='outlined' fullWidth>
                        <Controller
                          name='number_sim1'
                          control={control}
                          render={({ field }) => (
                            <Select
                              label=''
                              IconComponent={KeyboardArrowDownRounded}
                              readOnly={!editPermission}
                              MenuProps={{
                                sx: {
                                  '.MuiPaper-root': {
                                    maxHeight: 200,
                                    overflow: 'auto',
                                  },
                                  '.MuiPaper-root:before': {
                                    display: 'none',
                                  },
                                  '.MuiMenuItem-root': {
                                    gap: '12px',
                                    justifyContent: 'space-between',
                                  },
                                  img: {
                                    width: '20px',
                                    height: '20px',
                                  },
                                },
                              }}
                              sx={{
                                '.MuiTypography-root': {
                                  display: 'none',
                                },
                                img: {
                                  width: '20px',
                                  height: '20px',
                                },
                              }}
                              value={field.value}
                              onChange={field.onChange}
                              {...register(field.name, {
                                required: true,
                              })}
                              {...field}
                              ref={() => {
                                field.ref({
                                  focus: () => ({}),
                                });
                              }}>
                              {config.transportPhoneNumberOptions.map((value, key) => (
                                <MenuItem key={key} disableRipple value={value.code}>
                                  <Typography variant='body2'>{value.title}</Typography>
                                  <Image name={`${value.code}.svg`} alt={value.code} />
                                </MenuItem>
                              ))}
                            </Select>
                          )}></Controller>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <FormHelperText>Используйте номер телефона терминала {placeholder.join(', ')}</FormHelperText>
                </Grid>
              );
            })()}
          </React.Fragment>
        </Grid>
      )}
      {(fields.length !== 0 || !defaultValues) && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl margin='normal' variant='outlined' fullWidth>
              <InputLabel disableAnimation={firstUpdate}>Количество секций</InputLabel>
              <Select
                label='Количество секций'
                onChange={handleTankChange}
                defaultValue=''
                value={numberOfTank}
                notched={firstUpdate}
                IconComponent={KeyboardArrowDownRounded}
                readOnly={!editPermission}
                MenuProps={{
                  sx: {
                    '.MuiPaper-root:before': {
                      display: 'none',
                    },
                  },
                }}>
                <MenuItem value=''>Нет</MenuItem>
                {config.numberOfTank.map((value, key) => (
                  <MenuItem key={key} disableRipple value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Выберите количество секций цистерны, если транспортное средство имеет рамную конструкцию. В ином случае, оставьте поле
                незаполненным
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      )}
      {(fields.length !== 0 || !defaultValues) &&
        fields.map((field, i) => (
          <React.Fragment key={i}>
            <Divider
              textAlign='center'
              sx={(theme) => ({
                marginTop: theme.spacing(1.25),
              })}>
              <Typography variant='body2'>Секция {i + 1}</Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl error={!!formState.errors.tank?.[i]?.volume} margin='normal' variant='outlined' fullWidth>
                  <InputLabel disableAnimation={firstUpdate}>Объем секции</InputLabel>
                  <Controller
                    name={`tank.${i}.volume`}
                    control={control}
                    render={({ field }) => (
                      <InputMask
                        mask='99999'
                        maskChar=''
                        readOnly={!editPermission}
                        {...register(field.name, {
                          pattern: /^[0-9]{1,5}$/iu,
                          required: true,
                        })}
                        {...field}>
                        {(inputProps) => (
                          <OutlinedInput
                            label='Объем секции'
                            variant='outlined'
                            notched={firstUpdate}
                            onChange={field.onChange}
                            {...inputProps}
                            ref={() => {
                              field.ref({
                                focus: () => ({}),
                              });
                            }}></OutlinedInput>
                        )}
                      </InputMask>
                    )}></Controller>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  error={!!formState.errors.tank?.[i]?.type}
                  margin='normal'
                  variant='outlined'
                  fullWidth
                  sx={{ marginTop: '16px!important' }}>
                  <InputLabel disableAnimation={firstUpdate}>Тип топлива</InputLabel>
                  <Controller
                    name={`tank.${i}.type`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        label='Тип топлива'
                        IconComponent={KeyboardArrowDownRounded}
                        readOnly={!editPermission}
                        MenuProps={{
                          sx: {
                            '.MuiPaper-root:before': {
                              display: 'none',
                            },
                          },
                        }}
                        {...register(field.name, {
                          required: true,
                        })}
                        notched={firstUpdate}
                        {...field}>
                        {['АИ', 'ДТ', 'Битум'].map((value, key) => (
                          <MenuItem key={key} disableRipple value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    )}></Controller>
                </FormControl>
              </Grid>
            </Grid>
          </React.Fragment>
        ))}
    </form>
  );
});

export default TransportForm;
